import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

const StoryReader = ({ story, formToken }) => {

  const [isLoading, setIsLoading] = useState(false);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customChoice, setCustomChoice] = useState('');
  const [currentStory, setCurrentStory] = useState(story);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  const [currentCharIndex, setCurrentCharIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioTimestamps, setAudioTimestamps] = useState(null);
  const [coverLoading, setCoverLoading] = useState(!story.cover?.image_url);
  const [isCompleted, setIsCompleted] = useState(story.completed || false);
  const [canEnd, setCanEnd] = useState(story.can_end || false);
  const [isPublic, setIsPublic] = useState(story.is_public || false);
  const [endingBook, setEndingBook] = useState(false);
  const [sharingBook, setSharingBook] = useState(false);
  const [showEndOptions, setShowEndOptions] = useState(false);
  const [customEnding, setCustomEnding] = useState('');
  const audioRef = useRef(null);
  const wordsRef = useRef([]);

  // Update canEnd when story updates (use server's can_end value)
  useEffect(() => {
    setCanEnd(currentStory.can_end && !isCompleted);
  }, [currentStory.can_end, isCompleted]);

  // Check if current page is the final page (no choices and story is completed)
  const currentPageIndex = currentStory.current_page - 1;
  const currentPage = currentStory.pages?.[currentPageIndex];
  const isFinalPage = currentPage && (!currentPage.choices || currentPage.choices.length === 0) && currentPage.is_final;

  // Poll for cover image if it's not available yet
  useEffect(() => {
    if (!currentStory.cover?.image_url && currentStory.current_page === 0) {
      const pollInterval = setInterval(async () => {
        try {
          const response = await axios.get(`/adventure_stories/${currentStory.id}.json`);
          if (response.data.cover?.image_url) {
            setCurrentStory(prev => ({
              ...prev,
              cover: response.data.cover
            }));
            setCoverLoading(false);
            clearInterval(pollInterval);
          }
        } catch (error) {
          console.error('Error polling for cover:', error);
        }
      }, 3000); // Poll every 3 seconds

      return () => clearInterval(pollInterval);
    }
  }, [currentStory.id, currentStory.cover?.image_url, currentStory.current_page]);



  // Handle a choice selection
  const handleChoiceSelection = async (choiceIndex) => {
    setIsLoading(true);

    try {
      const response = await axios.post(
        `/adventure_stories/${currentStory.id}/choose_path`,
        {
          choice_index: choiceIndex,
          current_page: currentStory.current_page
        },
        {
          headers: {
            "X-CSRF-Token": formToken,
            "Content-Type": "application/json"
          }
        }
      );

      setIsLoading(false);
      setCurrentStory(response.data);
    } catch (error) {
      console.error("Error selecting choice:", error);
      setIsLoading(false);
      alert("Something went wrong. Please try again.");
    }
  };

  // Handle a custom choice submission
  const handleCustomChoice = async () => {
    if (!customChoice.trim()) return;

    setIsLoading(true);

    try {
      const response = await axios.post(
        `/adventure_stories/${currentStory.id}/choose_path`,
        {
          custom_choice: customChoice,
          current_page: currentStory.current_page
        },
        {
          headers: {
            "X-CSRF-Token": formToken,
            "Content-Type": "application/json"
          }
        }
      );

      setIsLoading(false);
      setShowCustomInput(false);
      setCustomChoice('');
      setCurrentStory(response.data);
    } catch (error) {
      console.error("Error submitting custom choice:", error);
      setIsLoading(false);
      alert("Something went wrong. Please try again.");
    }
  };

  // Handle ending the book - generates final page
  const handleEndBook = async (endingType = 'auto', customEndingText = '') => {
    if (!canEnd || endingBook) return;

    setEndingBook(true);
    setShowEndOptions(false);

    try {
      const response = await axios.post(
        `/adventure_stories/${currentStory.id}/choose_path`,
        {
          ending_type: endingType,
          custom_ending: customEndingText,
          current_page: currentStory.current_page
        },
        {
          headers: {
            "X-CSRF-Token": formToken,
            "Content-Type": "application/json"
          }
        }
      );

      // Update the story with the final page
      setCurrentStory(response.data);
      setIsCompleted(response.data.completed || false);
      setCanEnd(false);
    } catch (error) {
      console.error("Error ending book:", error);
      alert("Something went wrong. Please try again.");
    }
    setEndingBook(false);
    setCustomEnding('');
  };

  // Handle sharing the book
  const handleShareBook = async () => {
    if (!isCompleted || sharingBook) return;

    setSharingBook(true);
    try {
      const response = await axios.patch(
        `/adventure_stories/${currentStory.id}/share`,
        {},
        {
          headers: {
            "X-CSRF-Token": formToken,
            "Content-Type": "application/json"
          }
        }
      );

      if (response.data.success) {
        setIsPublic(true);
        alert("Your book is now shared with other readers!");
      }
    } catch (error) {
      console.error("Error sharing book:", error);
      alert("Something went wrong. Please try again.");
    }
    setSharingBook(false);
  };

    // Generate audio for the current page
  const generateAudio = async () => {
    const currentPageIndex = currentStory.current_page - 1;
    const currentPage = currentStory.pages[currentPageIndex];

    if (!currentPage) {
      return;
    }

    setAudioLoading(true);
    try {
      const response = await axios.post(
        `/adventure_story_pages/${currentPage.id}/generate_audio`,
        {},
        {
          headers: {
            "X-CSRF-Token": formToken,
            "Content-Type": "application/json"
          }
        }
      );

      if (response.data.success) {

        setAudioUrl(response.data.audio_url);
        setAudioTimestamps(response.data.audio_timestamps);
        // Reset word tracking state
        setCurrentWordIndex(-1);
        setCurrentCharIndex(-1);
        setIsPlaying(false);
      } else {
        alert("Failed to generate audio. Please try again.");
      }
    } catch (error) {
      console.error("Error generating audio:", error);
      alert("Failed to generate audio. Please try again.");
    } finally {
      setAudioLoading(false);
    }
  };

  // Split text into words and calculate timing
  const splitIntoWords = (text) => {
    return text.split(/(\s+)/).filter(word => word.trim().length > 0);
  };

  // Handle audio time updates for word tracking
  const handleTimeUpdate = () => {
    if (!audioRef.current) {
      return;
    }

    const currentTime = audioRef.current.currentTime;
    const audioIsPlaying = !audioRef.current.paused;

    // Update playing state based on actual audio state
    if (audioIsPlaying !== isPlaying) {
      setIsPlaying(audioIsPlaying);
    }

    if (audioTimestamps && audioIsPlaying) {
      // Use precise character-level timestamps
      const { characters, character_start_times_seconds } = audioTimestamps;

      // Find the current character index based on timestamp
      let newCharIndex = -1;
      for (let i = 0; i < character_start_times_seconds.length; i++) {
        if (currentTime >= character_start_times_seconds[i]) {
          newCharIndex = i;
        } else {
          break;
        }
      }

      if (newCharIndex !== currentCharIndex) {
        setCurrentCharIndex(newCharIndex);

        // Also update word index for fallback highlighting
        const currentText = characters.slice(0, newCharIndex + 1).join('');
        const currentWordCount = currentText.split(/\s+/).filter(word => word.length > 0).length;
        setCurrentWordIndex(Math.max(0, currentWordCount - 1));
      }
    } else if (!audioTimestamps && audioIsPlaying) {
      // Fall back to estimated word timing
      const duration = audioRef.current.duration;

      if (duration && wordsRef.current.length > 0) {
        const progress = currentTime / duration;
        const estimatedWordIndex = Math.floor(progress * wordsRef.current.length);

        if (estimatedWordIndex !== currentWordIndex && estimatedWordIndex < wordsRef.current.length) {
          setCurrentWordIndex(estimatedWordIndex);
        }
      }
    }
  };

  // Handle audio play/pause events
  const handlePlay = () => {
    setIsPlaying(true);
  };
  const handlePause = () => {
    setIsPlaying(false);
  };
  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentWordIndex(-1);
    setCurrentCharIndex(-1);
  };

  const handleLoadedData = () => {
    // Audio loaded
  };

  // Render text with highlighted characters or words
  const renderTrackedText = (text) => {
    if (audioTimestamps) {
      // Use character-level highlighting with precise timestamps
      const { characters } = audioTimestamps;

      return characters.map((char, index) => (
        <span
          key={index}
          className={`${
            index <= currentCharIndex
              ? 'bg-yellow-200 text-yellow-900'
              : ''
          } transition-all duration-150`}
        >
          {char}
        </span>
      ));
    } else {
      // Fall back to word-level highlighting
      const words = splitIntoWords(text);
      wordsRef.current = words;

      return words.map((word, index) => (
        <span
          key={index}
          className={`${
            index === currentWordIndex
              ? 'bg-red-300 text-red-900 rounded px-1 font-bold border-2 border-red-500'
              : ''
          } transition-all duration-200`}
        >
          {word}
        </span>
      ));
    }
  };

  // Show the cover page
  if (currentStory.current_page === 0) {
    return (
      <div className="max-w-4xl mx-auto flex flex-col items-center">
        <div className="bg-white rounded-xl shadow-xl overflow-hidden w-full max-w-2xl">
          {currentStory.cover?.image_url ? (
            <img
              src={currentStory.cover.image_url}
              alt={currentStory.cover.title}
              className="w-full object-cover h-96"
            />
          ) : (
            <div className="w-full h-96 bg-gradient-to-br from-indigo-100 via-purple-100 to-pink-100 flex flex-col items-center justify-center">
              <div className="relative">
                <div className="text-6xl animate-bounce">🎨</div>
                <div className="absolute -top-2 -right-2 text-2xl animate-pulse">✨</div>
              </div>
              <p className="mt-4 text-lg font-medium text-indigo-700">Your cover is being drawn...</p>
              <p className="mt-2 text-sm text-indigo-500">This usually takes about 10 seconds</p>
              <div className="mt-4 flex space-x-1">
                <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-pink-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          )}
          <div className="p-8 text-center">
            <h1 className="text-4xl font-bold mb-3 text-indigo-900">{currentStory.cover?.title}</h1>
            <p className="text-xl italic text-gray-600 mb-8">{currentStory.cover?.subtitle}</p>

            {isLoading ? (
              <div className="flex justify-center items-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
                <p className="ml-3 text-indigo-700">Opening book...</p>
              </div>
            ) : (
              <button
                onClick={() => handleChoiceSelection('start')}
                className="px-8 py-3 bg-indigo-600 text-white rounded-lg text-xl font-semibold hover:bg-indigo-700 transition-colors"
              >
                Begin Your Adventure
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Handle missing page
  if (!currentPage) {
    return (
      <div className="max-w-4xl mx-auto p-8 bg-white rounded-xl shadow-lg">
        <h2 className="text-xl font-bold text-red-500">Page not found</h2>
        <p className="mt-2">We couldn't find this page in your story.</p>
        <a
          href="/adventure_stories"
          className="mt-4 inline-block px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600"
        >
          Start a New Adventure
        </a>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-xl shadow-xl overflow-hidden mb-8">
        <div className="bg-indigo-100 px-6 py-3 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-indigo-800">Page {currentStory.current_page}</h2>
          <a
            href="/adventure_stories"
            className="text-indigo-600 hover:text-indigo-800"
          >
            Choose New Story
          </a>
        </div>

        {currentPage.image_url ? (
          <img
            src={currentPage.image_url}
            alt={`Illustration for page ${currentStory.current_page}`}
            className="w-full object-cover h-80"
          />
        ) : (
          <div className="h-32 bg-indigo-50 flex items-center justify-center">
            <p className="text-indigo-400">Image is being created...</p>
          </div>
        )}

        <div className="p-8">
          <p className="text-lg leading-relaxed mb-8">
            {audioUrl ? renderTrackedText(currentPage.content) : currentPage.content}
          </p>

          {/* Audio Controls */}
          <div className="mb-8">
            {audioLoading ? (
              <div className="flex items-center justify-center bg-indigo-100 rounded-full py-2 px-4">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-indigo-600 mr-2"></div>
                <span className="text-indigo-700 font-medium">Creating audio...</span>
              </div>
            ) : audioUrl ? (
              <div className="flex items-center bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl py-3 px-4 shadow-sm">
                <div className="mr-3 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full p-2 shadow-md">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15.536a5 5 0 001.414-7.072m-2.829 9.9a9 9 0 010-12.728" />
                  </svg>
                </div>
                <div className="flex-grow">
                  <div className="font-medium text-sm text-indigo-800 mb-1">
                    Listen to the story {isPlaying ? (audioTimestamps ? '(precise word tracking)' : '(following along)') : ''}
                  </div>
                  <audio
                    ref={audioRef}
                    controls
                    src={audioUrl}
                    className="w-full h-8"
                    preload="auto"
                    onLoadedData={handleLoadedData}
                    onTimeUpdate={handleTimeUpdate}
                    onPlay={handlePlay}
                    onPause={handlePause}
                    onEnded={handleEnded}
                  />
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="flex items-center bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white py-2 px-4 rounded-lg shadow-md transform transition-transform hover:scale-[1.02] duration-200"
                onClick={generateAudio}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15.536a5 5 0 001.414-7.072m-2.829 9.9a9 9 0 010-12.728" />
                </svg>
                <span>Read to me!</span>
              </button>
            )}
          </div>

          {/* Final Page Celebration - show when story is complete and on final page */}
          {isFinalPage && (
            <div className="mt-8">
              {/* Celebration Header */}
              <div className="text-center py-8 bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-50 rounded-2xl border-2 border-amber-200 mb-6">
                <div className="text-6xl mb-4">📚✨</div>
                <h2 className="text-3xl font-bold text-amber-800 mb-2">The End</h2>
                <p className="text-lg text-amber-700">You finished your adventure book!</p>
                <div className="flex justify-center gap-2 mt-4">
                  <span className="text-2xl">🎉</span>
                  <span className="text-2xl">🌟</span>
                  <span className="text-2xl">🎊</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-4">
                {!isPublic ? (
                  <button
                    onClick={handleShareBook}
                    disabled={sharingBook}
                    className="w-full p-5 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition disabled:opacity-50 flex items-center justify-center"
                  >
                    {sharingBook ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3"></div>
                        Sharing...
                      </>
                    ) : (
                      <>
                        <span className="mr-2 text-xl">📤</span>
                        Share Your Book with Other Kids
                      </>
                    )}
                  </button>
                ) : (
                  <div className="w-full p-5 bg-green-100 border-2 border-green-300 rounded-xl text-center">
                    <span className="text-green-700 font-semibold text-lg">✅ Your book is shared! Other kids can read it now.</span>
                  </div>
                )}

                <a
                  href="/adventure_stories"
                  className="block w-full p-5 bg-gradient-to-r from-indigo-100 to-purple-100 hover:from-indigo-200 hover:to-purple-200 border-2 border-indigo-200 rounded-xl font-semibold text-lg text-indigo-700 text-center transition"
                >
                  <span className="mr-2">📖</span>
                  Create Another Story
                </a>
              </div>
            </div>
          )}

          {/* Choices - only show if not on final page and not loading */}
          {!isFinalPage && currentPage?.choices && currentPage.choices.length > 0 && !isLoading && (
            <div className="mt-8">
              <h3 className="text-xl font-semibold mb-4 text-indigo-800">Choose Your Next Adventure</h3>

              <div className="space-y-3">
                {currentPage.choices.map((choice, index) => (
                  <button
                    key={index}
                    onClick={() => handleChoiceSelection(index)}
                    className="w-full text-left p-4 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-lg transition"
                  >
                    {choice}
                  </button>
                ))}

                {!showCustomInput ? (
                  <button
                    onClick={() => setShowCustomInput(true)}
                    className="w-full text-left p-4 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg transition flex items-center"
                  >
                    <span className="mr-2">✍️</span> Write your own path...
                  </button>
                ) : (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <textarea
                      value={customChoice}
                      onChange={(e) => setCustomChoice(e.target.value)}
                      placeholder="Write your own adventure direction..."
                      className="w-full p-3 border rounded-md focus:ring-2 focus:ring-indigo-500"
                      rows={3}
                    />
                    <div className="flex justify-end mt-2 space-x-2">
                      <button
                        onClick={() => {
                          setShowCustomInput(false);
                          setCustomChoice('');
                        }}
                        className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCustomChoice}
                        className="px-3 py-1 bg-indigo-500 hover:bg-indigo-600 text-white rounded"
                      >
                        Continue
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* End the Story Section - show after 5+ pages */}
              {canEnd && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-amber-800">Ready to finish?</h3>
                    <button
                      onClick={() => setShowEndOptions(!showEndOptions)}
                      className="text-amber-600 hover:text-amber-800 font-medium flex items-center"
                    >
                      {showEndOptions ? 'Hide options' : 'End the Story'}
                      <svg className={`w-4 h-4 ml-1 transition-transform ${showEndOptions ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>

                  {showEndOptions && (
                    <div className="space-y-3 animate-fadeIn">
                      {/* Auto-end option */}
                      <button
                        onClick={() => handleEndBook('auto')}
                        disabled={endingBook}
                        className="w-full p-4 bg-amber-50 hover:bg-amber-100 border-2 border-amber-300 rounded-xl transition text-left disabled:opacity-50"
                      >
                        <div className="flex items-center">
                          <span className="text-2xl mr-3">✨</span>
                          <div>
                            <div className="font-semibold text-amber-800">End it for me</div>
                            <div className="text-sm text-amber-600">Let the story write a perfect ending</div>
                          </div>
                        </div>
                      </button>

                      {/* Custom ending option */}
                      <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-xl">
                        <div className="flex items-center mb-3">
                          <span className="text-2xl mr-3">✍️</span>
                          <div>
                            <div className="font-semibold text-amber-800">Write your own ending</div>
                            <div className="text-sm text-amber-600">Tell us how you want the story to end</div>
                          </div>
                        </div>
                        <textarea
                          value={customEnding}
                          onChange={(e) => setCustomEnding(e.target.value)}
                          placeholder="How should your story end? (e.g., 'They all became best friends and lived happily ever after')"
                          className="w-full p-3 border border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                          rows={2}
                        />
                        <button
                          onClick={() => handleEndBook('custom', customEnding)}
                          disabled={endingBook || !customEnding.trim()}
                          className="mt-3 w-full py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {endingBook ? 'Creating ending...' : 'Finish My Story'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {isLoading && (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-500"></div>
              <p className="ml-3 text-lg text-indigo-700">Creating the next part of your adventure...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

StoryReader.propTypes = {
  story: PropTypes.shape({
    id: PropTypes.number.isRequired,
    current_page: PropTypes.number.isRequired,
    cover: PropTypes.shape({
      title: PropTypes.string.isRequired,
      subtitle: PropTypes.string,
      image_url: PropTypes.string
    }),
    pages: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        page_number: PropTypes.number,
        content: PropTypes.string,
        image_url: PropTypes.string,
        choices: PropTypes.arrayOf(PropTypes.string),
        is_final: PropTypes.bool
      })
    ),
    completed: PropTypes.bool,
    can_end: PropTypes.bool,
    is_public: PropTypes.bool
  }).isRequired,
  formToken: PropTypes.string.isRequired
};

export default StoryReader;